import jsPDF from 'jspdf';
import { Component, ChangeDetectorRef } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from "@angular/common/http";


// PrimeNG modules
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CalendarModule } from 'primeng/calendar';
import { AutoCompleteModule } from "primeng/autocomplete";
import { TableModule } from 'primeng/table';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { RadioButtonModule } from 'primeng/radiobutton';

import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';
import { BillMedsService } from '../../../service/admin/bill-meds.service';

interface Medicine {
  id: string;
  name: string;
  dose: string;
  quantity: string;
}

@Component({
  standalone: true,
  selector: 'app-bills',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    DropdownModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    CalendarModule,
    AutoCompleteModule,
    TableModule,
    InputTextareaModule,
    RadioButtonModule
  ],
  templateUrl: './bills.component.html',
  styleUrl: './bills.component.scss',
  providers: [BillMedsService]
})
export class BillsComponent {
  billForm!: FormGroup;
  medicinesList: Medicine[] = [];
  predefinedMedicines: Medicine[] = [];
  filteredMedicines: Medicine[] = [];

  // predefinedMedicines: Medicine[] = [
  //   { id: '1', name: 'Thyroidinum 30CH', dose: '3 drops', quantity: 1, duration: '7 days', price: 0 },
  //   { id: '2', name: 'Calcarea Carbonica 30CH', dose: '5 pellets', quantity: 1, duration: '14 days', price: 0 },
  //   { id: '3', name: 'Graphites 30CH', dose: '3 drops', quantity: 1, duration: '21 days', price: 0 },
  //   { id: '4', name: 'Sepia 30CH', dose: '5 pellets', quantity: 1, duration: '7 days', price: 0 },
  //   { id: '5', name: 'Sulphur 30CH', dose: '3 drops', quantity: 2, duration: '14 days', price: 0 },
  //   { id: '6', name: 'Apis Mellifica 30CH', dose: '5 pellets', quantity: 1, duration: '10 days', price: 0 },
  // ];
  
  selectedPaperSize: string = 'a4'; // Default to A4
  
  public patientsList: any[] = [];
  public selectedPatient: any = null;
  public selectedMedicinesToAdd: Medicine | null = null;

  consultationFee: number = 500;
  discountPercentage: number = 0;

  private apiUrl: string | undefined;

  // logo = '/logo.png'; // <-- for local
  logo = '/clinic/logo.png'; // <-- for production

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private apiService: ApiService,
    private toast: ToasterService,
    private billMedsService: BillMedsService,
    private cdr: ChangeDetectorRef
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
    this.initForm();
  }

  ngOnInit(): void {
    this.initForm();
    this.getBillMeds();
  }

  getBillMeds() {
    this.billMedsService.getBillMeds().subscribe((res: any) => {
      this.predefinedMedicines = (res?.data || []).map((medicine: any) => this.normalizeMedicine(medicine));
      this.filteredMedicines = [...this.predefinedMedicines];
    });
  }

  private normalizeMedicine(medicine: any): Medicine {
    const name = medicine?.name ?? medicine?.medicine ?? '';
    const quantity = medicine?.quantity ?? medicine?.qty ?? '';
    const dose = medicine?.dose ?? '';
    const id = medicine?.id ?? medicine?._id ?? `${name}-${quantity}-${dose}`;

    return {
      id: String(id),
      name: String(name),
      quantity: String(quantity),
      dose: String(dose),
    };
  }

  initForm(): void {
    this.billForm = this.fb.group({
      patientName: ['', Validators.required],
      date: [new Date(), Validators.required],
      consultationFee: [this.consultationFee, [Validators.required, Validators.min(0)]],
      prescriptionTitle: [''],
      prescriptionDuration: [''],
      notes: [''],
    });
  }

  searchPatients(event: any): void {
    const query = (event?.query || '').trim();
    if (!query) {
      this.patientsList = [];
      return;
    }

    this.http.get<any>(this.apiUrl + '/patient/search', {
      params: { q: query },
      ...this.getAuthHeaders()
    }).subscribe(
      res => {
        if (!res.data || res.data.length === 0) {
          this.patientsList = [];
          this.toast.add({
            severity: 'info',
            summary: 'Info',
            detail: `No patients found for "${query}".`
          });
        } else {
          this.patientsList = res.data.map((p: any) => ({
            ...p,
            full_name: [p.first_name, p.middle_name, p.last_name]
              .filter(Boolean)
              .join(' ')
              .concat(` (${p.patient_number})`)
          }));
        }
        this.cdr.detectChanges();
      },
      error => {
        this.patientsList = [];
        this.toast.add({
          severity: 'info',
          summary: 'Info',
          detail: 'No patients found.'
        });
        this.cdr.detectChanges();
      }
    );
  }

  searchMedicines(event: any): void {
    const query = (event?.query || '').trim().toLowerCase();

    if (!query) {
      this.filteredMedicines = [...this.predefinedMedicines];
      return;
    }

    this.filteredMedicines = this.predefinedMedicines.filter((medicine: Medicine) => {
      const haystack = `${medicine.name} ${medicine.dose} ${medicine.quantity}`.toLowerCase();
      return haystack.includes(query);
    });
  }

  onPatientSelect(event: any): void {
    const patient = event.value;
    this.selectedPatient = patient;
    if (!patient) return;

    const fullName = [patient.first_name, patient.middle_name, patient.last_name]
      .filter(Boolean)
      .join(' ');

    this.billForm.patchValue({
      patientName: fullName
    });
  }

  addMedicine(): void {
    if (!this.selectedMedicinesToAdd) {
      this.toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a medicine from the list.'
      });
      return;
    }

    // Check if medicine already exists
    const exists = this.medicinesList.find(m => m.id === this.selectedMedicinesToAdd!.id);
    if (exists) {
      this.toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'This medicine is already added.'
      });
      return;
    }

    this.medicinesList = [
      ...this.medicinesList,
      this.normalizeMedicine(this.selectedMedicinesToAdd)
    ];

    this.selectedMedicinesToAdd = null;
    this.filteredMedicines = [...this.predefinedMedicines];
    this.toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Medicine added to bill.'
    });
  }

  removeMedicine(index: number): void {
    this.medicinesList.splice(index, 1);
    this.medicinesList = [...this.medicinesList];
  }


  addRandomMedicine(): void {
    if (!this.predefinedMedicines.length) {
      this.toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No predefined medicines available.'
      });
      return;
    }

    const randomMedicine = this.predefinedMedicines[
      Math.floor(Math.random() * this.predefinedMedicines.length)
    ];

    const exists = this.medicinesList.find(m => m.id === randomMedicine.id);
    if (exists) {
      this.toast.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'This medicine is already added.'
      });
      return;
    }

    this.medicinesList = [
      ...this.medicinesList,
      this.normalizeMedicine(randomMedicine)
    ];

    this.toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `${randomMedicine.name} added randomly.`
    });
  }

  updateMedicineField(index: number, field: keyof Medicine, value: any): void {
    if (index >= 0 && index < this.medicinesList.length) {
      const medicine = this.medicinesList[index];
      (medicine[field] as any) = value;
      this.medicinesList = [...this.medicinesList];
    }
  }

  getDiscountAmount(): number {
    const consultationFee = this.billForm.get('consultationFee')?.value || 0;
    return (consultationFee * this.discountPercentage) / 100;
  }

  getTotalAmount(): number {
    const consultationFee = this.billForm.get('consultationFee')?.value || 0;
    return consultationFee - this.getDiscountAmount();
  }

  generateBill(): void {
    if (!this.billForm.valid || this.medicinesList.length === 0) {
      this.toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all required fields and add at least one medicine.'
      });
      return;
    }

    this.generatePDFlocally();

    this.toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Bill generated successfully! (Paper Size: ${this.selectedPaperSize.toUpperCase()})`
    });
  }

  resetBill(): void {
    this.initForm();
    this.medicinesList = [];
    this.selectedPatient = null;
    this.selectedMedicinesToAdd = null;
    this.discountPercentage = 0;
    this.consultationFee = 500;
    this.billForm.patchValue({ prescriptionTitle: '' });
    this.billForm.patchValue({ prescriptionDuration: '' });
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    };
  }

  generatePDFlocally() {
    const f = {
      ...this.billForm.value,
      medicinesList: this.medicinesList,
      consultationFee: this.billForm.get('consultationFee')?.value,
      discountPercentage: this.discountPercentage,
      discountAmount: this.getDiscountAmount(),
      totalAmount: this.getTotalAmount(),
    };
    const paperSize = this.selectedPaperSize || 'a5';
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: paperSize,
    });
    const img = new Image();

    // img.src = '/logo.png'; // <-- for local
    img.src = '/clinic/logo.png'; // <-- for production

    if (paperSize === 'a4') {
      img.onload = () => this.buildPDFA4(doc, f, img);
      img.onerror = () => this.buildPDFA4(doc, f);
    } else {
      img.onload = () => this.buildPDFA5(doc, f, img);
      img.onerror = () => this.buildPDFA5(doc, f);
    }
  }

  private drawTable(
    doc: jsPDF,
    x: number,
    y: number,
    columns: { label: string; width: number }[],
    rows: string[][],
    options?: {
      headerBg?: [number, number, number];
      rowHeight?: number;
      fontSize?: number;
      boldHeader?: boolean;
    }
  ): number {
    const rowH = options?.rowHeight ?? 8;
    const fontSize = options?.fontSize ?? 9;
    const headerBg = options?.headerBg ?? [245, 245, 245];

    const totalWidth = columns.reduce((s, c) => s + c.width, 0);

    // HEADER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.1);
    doc.setFillColor(...headerBg);
    doc.rect(x, y, totalWidth, rowH, 'FD');

    let cx = x;
    columns.forEach(col => {
      doc.line(cx, y, cx, y + rowH);
      doc.setFont('helvetica', options?.boldHeader === false ? 'normal' : 'bold');
      doc.setFontSize(fontSize);
      doc.text(col.label, cx + 2, y + 5);
      cx += col.width;
    });
    doc.line(x + totalWidth, y, x + totalWidth, y + rowH);

    y += rowH;

    // ROWS
    doc.setFont('helvetica', 'normal');
    rows.forEach(row => {
      doc.rect(x, y, totalWidth, rowH, 'S');

      let cx = x;
      row.forEach((cell, i) => {
        doc.line(cx, y, cx, y + rowH);
        doc.text(cell, cx + 2, y + 5);
        cx += columns[i].width;
      });
      doc.line(x + totalWidth, y, x + totalWidth, y + rowH);

      y += rowH;
    });

    return y;
  }


  private buildPDFA4(doc: jsPDF, f: any, img?: HTMLImageElement) {
    // Pixel-perfect layout based on certificates component
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 15;
    let y = 15;

    const brandPink: [number, number, number] = [227, 28, 121];
    const brandOrange: [number, number, number] = [246, 137, 32];
    const black: [number, number, number] = [0, 0, 0];
    const lightGray: [number, number, number] = [245, 245, 245];
    const darkGray: [number, number, number] = [51, 51, 51];

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);

    // Header
    if (img) {
      const imgWidth = (pageWidth - marginX * 2) * 0.25;
      const imgHeight = imgWidth * 0.4;
      doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
    } else {
      doc.setFillColor(...brandPink);
      doc.roundedRect(marginX, y, 35, 35, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.text('CLINIC', marginX + 17.5, y + 15, { align: 'center' });
      doc.text('LOGO', marginX + 17.5, y + 23, { align: 'center' });
    }
    
    const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.25 + 12 : 50);
    doc.setTextColor(...darkGray);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);

    const lineHeight = 4.5;
    doc.text('Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,', headerTextX, y + 8);
    doc.text('Maharshi Nagar, Pune, Maharashtra – 411037', headerTextX, y + 8 + lineHeight);
    doc.text('Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com', headerTextX, y + 8 + (lineHeight * 2));

    y += 23;

    // Border bottom
    doc.setLineWidth(1);
    doc.setDrawColor(...brandPink);
    doc.line(marginX, y, pageWidth / 2, y);
    doc.setDrawColor(...brandOrange);
    doc.line(pageWidth / 2, y, pageWidth - marginX, y);

    y += 2.5;

    // Sub header
    const halfWidth = (pageWidth - marginX * 2) / 2;
    const subHeaderHeight = 7;
    doc.setFillColor(...brandPink);
    doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');
    doc.setFillColor(...brandOrange);
    doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text('SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES', marginX + 8, + subHeaderHeight / 2 + 2.5);
    doc.text('Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm', pageWidth - marginX - 8, + subHeaderHeight / 2 + 2.5, { align: 'right' });

    y += subHeaderHeight + 15;

    // Patient info
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...darkGray);
    doc.text('Patient Name:', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.patientName || '', marginX + 35, y);
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', pageWidth / 2 + 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.date ? new Date(f.date).toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'}) : '', pageWidth / 2 + 45, y);

    y += 8;

    if(f.prescriptionTitle) {
      doc.setFont('helvetica', 'bold');
      doc.text('Prescription For:', marginX, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.prescriptionTitle || '', marginX + 45, y);
    }

    if(f.prescriptionDuration) {
      doc.setFont('helvetica', 'bold');
      doc.text('Duration:', pageWidth / 2 + 10, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.prescriptionDuration || '', pageWidth / 2 + 45, y);
    }

    y += 8;

    // Table: Charges
    const chargesCols = [
      { label: 'Description', width: (pageWidth - marginX * 2) * 0.7 },
      { label: 'Amount (Rs.)', width: (pageWidth - marginX * 2) * 0.3 }
      // { label: 'Amount (₹)', width: (pageWidth - marginX * 2) * 0.3 }
    ];

    y = this.drawTable(
      doc,
      marginX,
      y,
      chargesCols,
      [[
        'Consultation Fee',
        `Rs.${f.consultationFee}`
      ]]
    );

    y += 8;


    // y += 10;

    // Medicines table
    if (f.medicinesList && f.medicinesList.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...darkGray);
      doc.text('Medicines Prescribed', marginX, y);
      y += 3;

      const tableW = pageWidth - marginX * 2;

      const medicineCols = [
        { label: 'Medicine', width: tableW * 0.5 },
        { label: 'Quantity', width: tableW * 0.2 },
        { label: 'Dose', width: tableW * 0.3 },
        // { label: 'Duration', width: tableW * 0.25 }
      ];

      const medicineRows = f.medicinesList.map((m: any) => [
        m.name,
        String(m.quantity),
        m.dose,
      ]);

      y = this.drawTable(
        doc,
        marginX,
        y,
        medicineCols,
        medicineRows
      );

      y += 6;

    }

    // Summary table
    // y += 4;

    const tableW = pageWidth - marginX * 2;

    doc.setFont('helvetica', 'bold');
    const summaryCols = [
      { label: 'Summary', width: tableW * 0.7 },
      { label: '', width: tableW * 0.3 }
    ];

    const summaryRows: string[][] = [];

    if (f.discountPercentage > 0) {
      summaryRows.push([
        `Discount (${f.discountPercentage}%)`,
        `-Rs.${f.discountAmount}`
      ]);
    }

    summaryRows.push([
      'Total Amount',
      `Rs.${f.totalAmount}`
    ]);

    y = this.drawTable(
      doc,
      marginX,
      y,
      summaryCols,
      summaryRows,
      { boldHeader: false }
    );


    y += 8;

    // Notes section
    if (f.notes) {
      doc.setDrawColor(...black);
      doc.setLineWidth(0.1);
      // doc.setLineDash([2, 2], 0);
      doc.rect(marginX, y, pageWidth - marginX * 2, 25, 'S');
      // doc.setLineDash([]);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Notes:', marginX + 2, y + 6);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const splitNotes = doc.splitTextToSize(f.notes, pageWidth - marginX * 2 - 4);
      doc.text(splitNotes, marginX + 2, y + 12);
    }

    // Footer signature
    const footerY = Math.max(y + 30, pageHeight - 40);
    const sigWidth = 70;
    const sigX = pageWidth - marginX - sigWidth;

    doc.setDrawColor(...darkGray);
    doc.setLineWidth(1);
    doc.line(sigX, footerY, pageWidth - marginX, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(...darkGray);
    doc.text('Dr. Shailesh Sancheti', sigX, footerY + 8);
    doc.setFontSize(9);
    doc.text('MD (Homeopathy)', sigX, footerY + 14);
    // doc.save('bill.pdf');
    const safePatientName = (f.patientName || 'Patient')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();

    doc.save(`${safePatientName}-bill.pdf`);
  }

  // private buildPDFA4(doc: jsPDF, f: any, img?: HTMLImageElement) {
  //   // Pixel-perfect layout based on certificates component
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const pageHeight = doc.internal.pageSize.getHeight();

  //   const marginX = 15;
  //   let y = 15;

  //   const brandPink: [number, number, number] = [227, 28, 121];
  //   const brandOrange: [number, number, number] = [246, 137, 32];
  //   const black: [number, number, number] = [0, 0, 0];
  //   const lightGray: [number, number, number] = [245, 245, 245];
  //   const darkGray: [number, number, number] = [51, 51, 51];

  //   doc.setFont('helvetica', 'normal');
  //   doc.setTextColor(...darkGray);

  //   // Header
  //   if (img) {
  //     const imgWidth = (pageWidth - marginX * 2) * 0.25;
  //     const imgHeight = imgWidth * 0.4;
  //     doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
  //   } else {
  //     doc.setFillColor(...brandPink);
  //     doc.roundedRect(marginX, y, 35, 35, 3, 3, 'F');
  //     doc.setFont('helvetica', 'bold');
  //     doc.setTextColor(255, 255, 255);
  //     doc.setFontSize(10);
  //     doc.text('CLINIC', marginX + 17.5, y + 15, { align: 'center' });
  //     doc.text('LOGO', marginX + 17.5, y + 23, { align: 'center' });
  //   }
    
  //   const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.25 + 12 : 50);
  //   doc.setTextColor(...darkGray);
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(10);

  //   const lineHeight = 4.5;
  //   doc.text('Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,', headerTextX, y + 8);
  //   doc.text('Maharshi Nagar, Pune, Maharashtra – 411037', headerTextX, y + 8 + lineHeight);
  //   doc.text('Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com', headerTextX, y + 8 + (lineHeight * 2));

  //   y += 23;

  //   // Border bottom
  //   doc.setLineWidth(2);
  //   doc.setDrawColor(...brandPink);
  //   doc.line(marginX, y, pageWidth / 2, y);
  //   doc.setDrawColor(...brandOrange);
  //   doc.line(pageWidth / 2, y, pageWidth - marginX, y);

  //   y += 2.5;

  //   // Sub header
  //   const halfWidth = (pageWidth - marginX * 2) / 2;
  //   const subHeaderHeight = 10;
  //   doc.setFillColor(...brandPink);
  //   doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');
  //   doc.setFillColor(...brandOrange);
  //   doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(8);
  //   doc.setTextColor(255, 255, 255);
  //   doc.text('SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES', marginX + 8, y + 6.5);
  //   doc.text('Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm', pageWidth - marginX - 8, y + 6.5, { align: 'right' });

  //   y += subHeaderHeight + 15;

  //   // Patient info
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(12);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Patient Name:', marginX, y);
  //   doc.setFont('helvetica', 'normal');
  //   doc.text(f.patientName || '', marginX + 35, y);
  //   doc.setFont('helvetica', 'bold');
  //   doc.text('Date:', pageWidth / 2 + 10, y);
  //   doc.setFont('helvetica', 'normal');
  //   doc.text(f.date ? new Date(f.date).toLocaleDateString() : '', pageWidth / 2 + 45, y);

  //   y += 8;

  //   if(f.prescriptionDuration) {
  //     doc.setFont('helvetica', 'bold');
  //     doc.text('Prescription Duration:', marginX, y);
  //     doc.setFont('helvetica', 'normal');
  //     doc.text(f.prescriptionDuration || '', marginX + 45, y);
  //   }

  //   y += 8;

  //   // Table: Charges
  //   // doc.setFont('helvetica', 'bold');
  //   // doc.setFontSize(11);
  //   // doc.setTextColor(...darkGray);
  //   // doc.text('Charges', marginX, y);

  //   // Charges table header
  //   doc.setLineWidth(0.7);
  //   doc.setDrawColor(...black);
  //   doc.setFillColor(...lightGray);
  //   doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'FD');

  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(10);
  //   doc.text('Description', marginX + 2, y + 5);
  //   doc.text('Amount (₹)', pageWidth - marginX - 35, y + 5);

  //   y += 8;

  //   // doc.setLineWidth(0.5);
  //   // doc.setDrawColor(200, 200, 200);
  //   // doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //   // doc.setFont('helvetica', 'normal');
  //   // doc.setFontSize(10);
  //   // doc.text('Consultation Fee', marginX + 2, y + 5);
  //   // doc.text(`₹${f.consultationFee}`, pageWidth - marginX - 30, y + 5);

  //   // Charges row
  //   doc.setFillColor(255, 255, 255);
  //   doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');

  //   doc.setFont('helvetica', 'normal');
  //   doc.text('Consultation Fee', marginX + 2, y + 5);
  //   doc.text(`₹${f.consultationFee}`, pageWidth - marginX - 30, y + 5);

  //   y += 10;

  //   // Medicines table
  //   if (f.medicinesList && f.medicinesList.length > 0) {
  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(10);
  //     doc.setTextColor(...darkGray);
  //     doc.text('Medicines Prescribed', marginX, y);
  //     y += 6;

  //     // Table header
  //     // doc.setLineWidth(0.5);
  //     // doc.setDrawColor(200, 200, 200);
  //     // doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //     // doc.setFont('helvetica', 'bold');
  //     // doc.setFontSize(9);
  //     // doc.setTextColor(...darkGray);
  //     // doc.text('Medicine', marginX + 2, y + 5);
  //     // doc.text('Dose', marginX + 40, y + 5);
  //     // doc.text('Quantity', marginX + 70, y + 5);
  //     // doc.text('Duration', marginX + 90, y + 5);
  //     // y += 8;

  //     doc.setLineWidth(0.7);
  //     doc.setDrawColor(...black);
  //     doc.setFillColor(...lightGray);
  //     doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'FD');

  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(9);
  //     doc.text('Medicine', marginX + 2, y + 5);
  //     doc.text('Dose', marginX + 40, y + 5);
  //     doc.text('Quantity', marginX + 75, y + 5);
  //     doc.text('Duration', marginX + 95, y + 5);

  //     y += 8;

  //     // Table rows
  //     // doc.setFont('helvetica', 'normal');
  //     // doc.setFontSize(8);
  //     // f.medicinesList.forEach((med: any) => {
  //     //   doc.setLineWidth(0.5);
  //     //   doc.setDrawColor(220, 220, 220);
  //     //   doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //     //   doc.text(med.name, marginX + 2, y + 5);
  //     //   doc.text(med.dose, marginX + 40, y + 5);
  //     //   doc.text(String(med.quantity), marginX + 70, y + 5);
  //     //   doc.text(med.duration, marginX + 90, y + 5);
  //     //   y += 8;
  //     // });

  //     doc.setFont('helvetica', 'normal');
  //     doc.setFontSize(9);

  //     f.medicinesList.forEach((med: any) => {
  //       doc.setFillColor(255, 255, 255);
  //       doc.setDrawColor(...black);
  //       doc.setLineWidth(0.5);
  //       doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');

  //       doc.text(med.name, marginX + 2, y + 5);
  //       doc.text(med.dose, marginX + 40, y + 5);
  //       doc.text(String(med.quantity), marginX + 75, y + 5);
  //       doc.text(med.duration, marginX + 95, y + 5);

  //       y += 8;
  //     });
  //   }

  //   // Summary table
  //   y += 4;

  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(10);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Summary', marginX, y);

  //   y += 6;

  //   // doc.setLineWidth(0.5);
  //   // doc.setDrawColor(200, 200, 200);
  //   // doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //   // doc.setFont('helvetica', 'normal');
  //   // doc.setFontSize(9);
  //   // doc.text('Subtotal', marginX + 2, y + 5);
  //   // doc.text(`₹${f.subtotal}`, pageWidth - marginX - 30, y + 5);

  //   // y += 8;

  //   // if (f.discountPercentage > 0) {
  //   //   doc.setLineWidth(0.5);
  //   //   doc.setDrawColor(200, 200, 200);
  //   //   doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //   //   doc.setFont('helvetica', 'normal');
  //   //   doc.setFontSize(9);
  //   //   doc.text(`Discount (${f.discountPercentage}%)`, marginX + 2, y + 5);
  //   //   doc.text(`-₹${f.discountAmount}`, pageWidth - marginX - 30, y + 5);
  //   //   y += 8;
  //   // }

  //   // doc.setLineWidth(0.5);
  //   // doc.setDrawColor(200, 200, 200);
  //   // doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');
  //   // doc.setFont('helvetica', 'bold');
  //   // doc.setFontSize(10);
  //   // doc.text('Total Amount', marginX + 2, y + 5);
  //   // doc.text(`₹${f.totalAmount}`, pageWidth - marginX - 30, y + 5);

  //   const summaryRow = (label: string, value: string, bold = false) => {
  //     doc.setDrawColor(...black);
  //     doc.setLineWidth(0.7);
  //     doc.rect(marginX, y, pageWidth - marginX * 2, 8, 'S');

  //     doc.setFont('helvetica', bold ? 'bold' : 'normal');
  //     doc.text(label, marginX + 2, y + 5);
  //     doc.text(value, pageWidth - marginX - 30, y + 5);

  //     y += 8;
  //   };

  //   summaryRow('Subtotal', `₹${f.subtotal}`);
  //   if (f.discountPercentage > 0) {
  //     summaryRow(`Discount (${f.discountPercentage}%)`, `-₹${f.discountAmount}`);
  //   }
  //   summaryRow('Total Amount', `₹${f.totalAmount}`, true);

  //   y += 12;

  //   // Notes section
  //   if (f.notes) {
  //     // doc.setFont('helvetica', 'bold');
  //     // doc.setFontSize(10);
  //     // doc.setTextColor(...darkGray);
  //     // doc.text('Notes:', marginX, y);
  //     // y += 6;
  //     // doc.setFont('helvetica', 'normal');
  //     // doc.setFontSize(9);
  //     // const notesText = f.notes.replace(/\s+/g, ' ').trim();
  //     // const splitNotes = doc.splitTextToSize(notesText, pageWidth - marginX * 2);
  //     // splitNotes.forEach((line: string, index: number) => {
  //     //   doc.text(line, marginX, y + (index * 5));
  //     // });
  //     // y += splitNotes.length * 5 + 8;

  //     doc.setDrawColor(...black);
  //     doc.setLineWidth(0.7);
  //     // doc.setLineDash([2, 2], 0);
  //     doc.rect(marginX, y, pageWidth - marginX * 2, 25, 'S');
  //     // doc.setLineDash([]);

  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(10);
  //     doc.text('Notes:', marginX + 2, y + 6);

  //     doc.setFont('helvetica', 'normal');
  //     doc.setFontSize(9);
  //     const splitNotes = doc.splitTextToSize(f.notes, pageWidth - marginX * 2 - 4);
  //     doc.text(splitNotes, marginX + 2, y + 12);
  //   }

  //   // Footer signature
  //   const footerY = Math.max(y + 30, pageHeight - 40);
  //   const sigWidth = 70;
  //   const sigX = pageWidth - marginX - sigWidth;

  //   doc.setDrawColor(...darkGray);
  //   doc.setLineWidth(1);
  //   doc.line(sigX, footerY, pageWidth - marginX, footerY);
  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(12);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Dr. Shailesh Sancheti', sigX, footerY + 8);
  //   doc.setFontSize(9);
  //   doc.text('MD (Homeopathy)', sigX, footerY + 14);
  //   doc.save('bill.pdf');
  // }

  private buildPDFA5(doc: jsPDF, f: any, img?: HTMLImageElement) {
    // Pixel-perfect layout based on certificates component
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 12;
    let y = 10;

    const brandPink: [number, number, number] = [227, 28, 121];
    const brandOrange: [number, number, number] = [246, 137, 32];
    const black: [number, number, number] = [0, 0, 0];
    const lightGray: [number, number, number] = [245, 245, 245];
    const darkGray: [number, number, number] = [51, 51, 51];

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);

    // Header
    if (img) {
      const imgWidth = (pageWidth - marginX * 2) * 0.3;
      const imgHeight = imgWidth * 0.4;
      doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
    } else {
      doc.setFillColor(...brandPink);
      doc.roundedRect(marginX, y, 22, 22, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.text('CLINIC', marginX + 11, y + 9, { align: 'center' });
      doc.text('LOGO', marginX + 11, y + 15, { align: 'center' });
    }

    const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.3 + 6 : 30);
    doc.setTextColor(...darkGray);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);

    const lineHeight = 3.5;
    doc.text('Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,', headerTextX, y + 5);
    doc.text('Maharshi Nagar, Pune, Maharashtra – 411037', headerTextX, y + 5 + lineHeight);
    doc.text('Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com', headerTextX, y + 5 + (lineHeight * 2));

    y += 20;

    // Border bottom
    doc.setLineWidth(1);
    doc.setDrawColor(...brandPink);
    doc.line(marginX, y, pageWidth / 2, y);
    doc.setDrawColor(...brandOrange);
    doc.line(pageWidth / 2, y, pageWidth - marginX, y);

    y += 1.5;

    // Sub header
    const halfWidth = (pageWidth - marginX * 2) / 2;
    const subHeaderHeight = 7;
    doc.setFillColor(...brandPink);
    doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');
    doc.setFillColor(...brandOrange);
    doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);
    doc.text('SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES', marginX + 3, y + 4.5);
    doc.text('Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm', pageWidth - marginX - 3, y + 4.5, { align: 'right' });

    y += subHeaderHeight + 6;

    // Patient info
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...darkGray);
    doc.text('Patient Name:', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.patientName || '', marginX + 30, y);
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', pageWidth / 2 + 5, y);
    doc.setFont('helvetica', 'normal');
    doc.text(f.date ? new Date(f.date).toLocaleDateString('en-GB', {day: 'numeric', month: 'long', year: 'numeric'}) : '', pageWidth / 2 + 35, y);

    y += 6;

    if(f.prescriptionTitle) {
      doc.setFont('helvetica', 'bold');
      doc.text('Prescription For:', marginX, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.prescriptionTitle || '', marginX + 40, y);
    }

    if(f.prescriptionDuration) {
      doc.setFont('helvetica', 'bold');
      doc.text('Duration:', pageWidth / 2 + 5, y);
      doc.setFont('helvetica', 'normal');
      doc.text(f.prescriptionDuration || '', pageWidth / 2 + 35, y);
    }

    y += 6;

    // Table: Charges
    // doc.setFont('helvetica', 'bold');
    // doc.setFontSize(8);
    // doc.setTextColor(...darkGray);
    // doc.text('Charges', marginX, y);

    // y += 5;

    // doc.setLineWidth(0.5);
    // doc.setDrawColor(200, 200, 200);
    // doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    // doc.setFont('helvetica', 'normal');
    // doc.setFontSize(8);
    // doc.text('Consultation Fee', marginX + 2, y + 4);
    // doc.text(`₹${f.consultationFee}`, pageWidth - marginX - 25, y + 4);

    // y += 8;

    const tableW = pageWidth - marginX * 2;

    const chargesCols = [
      { label: 'Description', width: tableW * 0.7 },
      { label: 'Amount (Rs.)', width: tableW * 0.3 }
    ];

    y = this.drawTable(
      doc,
      marginX,
      y,
      chargesCols,
      [[
        'Consultation Fee',
        `Rs.${f.consultationFee}`
      ]],
      {
        fontSize: 7,
        rowHeight: 7
      }
    );

    y += 6;


    // Medicines table
    // if (f.medicinesList && f.medicinesList.length > 0) {
    //   doc.setFont('helvetica', 'bold');
    //   doc.setFontSize(8);
    //   doc.setTextColor(...darkGray);
    //   doc.text('Medicines Prescribed', marginX, y);
    //   y += 5;
    //   // Table header
    //   doc.setLineWidth(0.5);
    //   doc.setDrawColor(200, 200, 200);
    //   doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    //   doc.setFont('helvetica', 'bold');
    //   doc.setFontSize(7);
    //   doc.setTextColor(...darkGray);
    //   doc.text('Medicine', marginX + 2, y + 4);
    //   doc.text('Dose', marginX + 30, y + 4);
    //   doc.text('Quantity', marginX + 55, y + 4);
    //   doc.text('Duration', marginX + 70, y + 4);
    //   y += 7;
    //   // Table rows
    //   doc.setFont('helvetica', 'normal');
    //   doc.setFontSize(7);
    //   f.medicinesList.forEach((med: any) => {
    //     doc.setLineWidth(0.5);
    //     doc.setDrawColor(220, 220, 220);
    //     doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    //     doc.text(med.name, marginX + 2, y + 4);
    //     doc.text(med.dose, marginX + 30, y + 4);
    //     doc.text(String(med.quantity), marginX + 55, y + 4);
    //     doc.text(med.duration, marginX + 70, y + 4);
    //     y += 7;
    //   });
    // }

    if (f.medicinesList && f.medicinesList.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text('Medicines Prescribed', marginX, y);
      y += 3;

      const medicineCols = [
        { label: 'Medicine', width: tableW * 0.5 },
        { label: 'Quantity', width: tableW * 0.2 },
        { label: 'Dose', width: tableW * 0.3 },
        // { label: 'Duration', width: tableW * 0.25 }
      ];

      const medicineRows = f.medicinesList.map((m: any) => [
        m.name,
        String(m.quantity),
        m.dose,
      ]);

      y = this.drawTable(
        doc,
        marginX,
        y,
        medicineCols,
        medicineRows,
        {
          fontSize: 7,
          rowHeight: 7
        }
      );

      y += 4;
    }


    // Summary table
    // y += 3;

    // doc.setFont('helvetica', 'bold');
    // doc.setFontSize(8);
    // doc.setTextColor(...darkGray);
    // doc.text('Summary', marginX, y);

    // y += 5;

    // doc.setLineWidth(0.5);
    // doc.setDrawColor(200, 200, 200);
    // doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    // doc.setFont('helvetica', 'normal');
    // doc.setFontSize(7);
    // doc.text('Subtotal', marginX + 2, y + 4);
    // doc.text(`₹${f.subtotal}`, pageWidth - marginX - 25, y + 4);

    // y += 7;

    // if (f.discountPercentage > 0) {
    //   doc.setLineWidth(0.5);
    //   doc.setDrawColor(200, 200, 200);
    //   doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    //   doc.setFont('helvetica', 'normal');
    //   doc.setFontSize(7);
    //   doc.text(`Discount (${f.discountPercentage}%)`, marginX + 2, y + 4);
    //   doc.text(`-₹${f.discountAmount}`, pageWidth - marginX - 25, y + 4);
    //   y += 7;
    // }

    // doc.setLineWidth(0.5);
    // doc.setDrawColor(200, 200, 200);
    // doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
    // doc.setFont('helvetica', 'bold');
    // doc.setFontSize(8);
    // doc.text('Total Amount', marginX + 2, y + 4);
    // doc.text(`₹${f.totalAmount}`, pageWidth - marginX - 25, y + 4);

    // y += 10;

    const summaryCols = [
      { label: 'Summary', width: tableW * 0.7 },
      { label: '', width: tableW * 0.3 }
    ];

    const summaryRows: string[][] = [];

    if (f.discountPercentage > 0) {
      summaryRows.push([
        `Discount (${f.discountPercentage}%)`,
        `-Rs.${f.discountAmount}`
      ]);
    }

    summaryRows.push([
      'Total Amount',
      `Rs.${f.totalAmount}`
    ]);

    y = this.drawTable(
      doc,
      marginX,
      y,
      summaryCols,
      summaryRows,
      {
        fontSize: 7,
        rowHeight: 7,
        boldHeader: false
      }
    );

    y += 4;


    // Notes section
    // if (f.notes) {
    //   doc.setDrawColor(...black);
    //   doc.setLineWidth(0.3);
    //   // doc.setLineDash([2, 2], 0);
    //   doc.rect(marginX, y, pageWidth - marginX * 2, 15, 'S');
      
    //   doc.setFont('helvetica', 'bold');
    //   doc.setFontSize(8);
    //   doc.setTextColor(...darkGray);
    //   doc.text('Notes:', marginX + 2, y + 6);
      
    //   doc.setFont('helvetica', 'normal');
    //   doc.setFontSize(7);
    //   const notesText = f.notes.replace(/\s+/g, ' ').trim();
    //   const splitNotes = doc.splitTextToSize(notesText, pageWidth - marginX * 2);
    //   splitNotes.forEach((line: string, index: number) => {
    //     doc.text(line, marginX, y + (index * 4));
    //   });
    //   y += splitNotes.length * 4 + 6;
    // }

    if (f.notes) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);

      const padding = 2;
      const lineHeight = 4;
      const maxTextWidth = pageWidth - marginX * 2 - padding * 2;

      const notesText = f.notes.replace(/\s+/g, ' ').trim();
      const splitNotes = doc.splitTextToSize(notesText, maxTextWidth);

      // Calculate dynamic box height
      const boxHeight =
        padding * 2 +          // top + bottom padding
        lineHeight * splitNotes.length +
        5;                     // space for "Notes:" label

      // Draw box
      doc.setDrawColor(...black);
      doc.setLineWidth(0.1);
      doc.rect(marginX, y, pageWidth - marginX * 2, boxHeight, 'S');

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...darkGray);
      doc.text('Notes:', marginX + padding, y + 5);

      // Content
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      splitNotes.forEach((line: string, index: number) => {
        doc.text(
          line,
          marginX + padding,
          y + 5 + lineHeight * (index + 1)
        );
      });

      // Advance Y correctly
      y += boxHeight + 6;
    }


    // if (f.notes) {
    //   doc.setDrawColor(...black);
    //   doc.setLineWidth(0.3);
    //   // doc.setLineDash([2, 2], 0);
    //   doc.rect(marginX, y, pageWidth - marginX * 2, 25, 'S');
    //   // doc.setLineDash([]);

    //   doc.setFont('helvetica', 'bold');
    //   doc.setFontSize(10);
    //   doc.text('Notes:', marginX + 2, y + 6);

    //   doc.setFont('helvetica', 'normal');
    //   doc.setFontSize(9);
    //   const splitNotes = doc.splitTextToSize(f.notes, pageWidth - marginX * 2 - 4);
    //   doc.text(splitNotes, marginX + 2, y + 12);
    // }

    // Footer signature
    const footerY = Math.max(y + 20, pageHeight - (45 + 25));
    const sigWidth = 45;
    const sigX = pageWidth - marginX - sigWidth;
    doc.setDrawColor(...darkGray);
    doc.setLineWidth(0.5);
    doc.line(sigX, footerY, sigX + sigWidth, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkGray);
    doc.text('Dr. Shailesh Sancheti', sigX, footerY + 7);
    doc.setFontSize(7);
    doc.text('MD (Homeopathy)', sigX, footerY + 12);
    // doc.save('bill.pdf');
    const safePatientName = (f.patientName || 'Patient')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();

    doc.save(`${safePatientName}-bill.pdf`);
  }

  // private buildPDFA5(doc: jsPDF, f: any, img?: HTMLImageElement) {
  //   // Pixel-perfect layout based on certificates component
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const pageHeight = doc.internal.pageSize.getHeight();

  //   const marginX = 12;
  //   let y = 10;

  //   const brandPink: [number, number, number] = [227, 28, 121];
  //   const brandOrange: [number, number, number] = [246, 137, 32];
  //   const black: [number, number, number] = [0, 0, 0];
  //   const lightGray: [number, number, number] = [245, 245, 245];
  //   const darkGray: [number, number, number] = [51, 51, 51];

  //   doc.setFont('helvetica', 'normal');
  //   doc.setTextColor(...darkGray);

  //   // Header
  //   if (img) {
  //     const imgWidth = (pageWidth - marginX * 2) * 0.3;
  //     const imgHeight = imgWidth * 0.4;
  //     doc.addImage(img, 'PNG', marginX, y, imgWidth, imgHeight);
  //   } else {
  //     doc.setFillColor(...brandPink);
  //     doc.roundedRect(marginX, y, 22, 22, 2, 2, 'F');
  //     doc.setFont('helvetica', 'bold');
  //     doc.setTextColor(255, 255, 255);
  //     doc.setFontSize(7);
  //     doc.text('CLINIC', marginX + 11, y + 9, { align: 'center' });
  //     doc.text('LOGO', marginX + 11, y + 15, { align: 'center' });
  //   }

  //   const headerTextX = marginX + (img ? (pageWidth - marginX * 2) * 0.3 + 6 : 30);
  //   doc.setTextColor(...darkGray);
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(7);

  //   const lineHeight = 3.5;
  //   doc.text('Sr. No. 2/14, Opp. Purandar (MHADA) Building, Near Paneerwala,', headerTextX, y + 5);
  //   doc.text('Maharshi Nagar, Pune, Maharashtra – 411037', headerTextX, y + 5 + lineHeight);
  //   doc.text('Ph: 8070 7080 01 / 02 / 03 | Email: drshaileshsancheti@gmail.com', headerTextX, y + 5 + (lineHeight * 2));

  //   y += 20;

  //   // Border bottom
  //   doc.setLineWidth(1);
  //   doc.setDrawColor(...brandPink);
  //   doc.line(marginX, y, pageWidth / 2, y);
  //   doc.setDrawColor(...brandOrange);
  //   doc.line(pageWidth / 2, y, pageWidth - marginX, y);

  //   y += 1.5;

  //   // Sub header
  //   const halfWidth = (pageWidth - marginX * 2) / 2;
  //   const subHeaderHeight = 7;
  //   doc.setFillColor(...brandPink);
  //   doc.rect(marginX, y, halfWidth, subHeaderHeight, 'F');
  //   doc.setFillColor(...brandOrange);
  //   doc.rect(marginX + halfWidth, y, halfWidth, subHeaderHeight, 'F');
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(6);
  //   doc.setTextColor(255, 255, 255);
  //   doc.text('SPECIALIZED IN THYROID, HAIR, SKIN & DIABETES', marginX + 3, y + 4.5);
  //   doc.text('Time: Morning 11 am – 2 pm | Evening 6 pm – 8 pm', pageWidth - marginX - 3, y + 4.5, { align: 'right' });

  //   y += subHeaderHeight + 6;

  //   // Patient info
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(8.5);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Patient Name:', marginX, y);
  //   doc.setFont('helvetica', 'normal');
  //   doc.text(f.patientName || '', marginX + 30, y);
  //   doc.setFont('helvetica', 'bold');
  //   doc.text('Date:', pageWidth / 2 + 5, y);
  //   doc.setFont('helvetica', 'normal');
  //   doc.text(f.date ? new Date(f.date).toLocaleDateString() : '', pageWidth / 2 + 35, y);

  //   y += 6;

  //   if(f.prescriptionDuration) {
  //     doc.setFont('helvetica', 'bold');
  //     doc.text('Duration:', marginX, y);
  //     doc.setFont('helvetica', 'normal');
  //     doc.text(f.prescriptionDuration || '', marginX + 40, y);
  //   }

  //   y += 6;

  //   // Table: Charges
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(8);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Charges', marginX, y);

  //   y += 5;

  //   doc.setLineWidth(0.5);
  //   doc.setDrawColor(200, 200, 200);
  //   doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(8);
  //   doc.text('Consultation Fee', marginX + 2, y + 4);
  //   doc.text(`₹${f.consultationFee}`, pageWidth - marginX - 25, y + 4);

  //   y += 8;

  //   // Medicines table
  //   if (f.medicinesList && f.medicinesList.length > 0) {
  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(8);
  //     doc.setTextColor(...darkGray);
  //     doc.text('Medicines Prescribed', marginX, y);
  //     y += 5;
  //     // Table header
  //     doc.setLineWidth(0.5);
  //     doc.setDrawColor(200, 200, 200);
  //     doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(7);
  //     doc.setTextColor(...darkGray);
  //     doc.text('Medicine', marginX + 2, y + 4);
  //     doc.text('Dose', marginX + 30, y + 4);
  //     doc.text('Quantity', marginX + 55, y + 4);
  //     doc.text('Duration', marginX + 70, y + 4);
  //     y += 7;
  //     // Table rows
  //     doc.setFont('helvetica', 'normal');
  //     doc.setFontSize(7);
  //     f.medicinesList.forEach((med: any) => {
  //       doc.setLineWidth(0.5);
  //       doc.setDrawColor(220, 220, 220);
  //       doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //       doc.text(med.name, marginX + 2, y + 4);
  //       doc.text(med.dose, marginX + 30, y + 4);
  //       doc.text(String(med.quantity), marginX + 55, y + 4);
  //       doc.text(med.duration, marginX + 70, y + 4);
  //       y += 7;
  //     });
  //   }

  //   // Summary table
  //   y += 3;

  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(8);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Summary', marginX, y);

  //   y += 5;

  //   doc.setLineWidth(0.5);
  //   doc.setDrawColor(200, 200, 200);
  //   doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(7);
  //   doc.text('Subtotal', marginX + 2, y + 4);
  //   doc.text(`₹${f.subtotal}`, pageWidth - marginX - 25, y + 4);

  //   y += 7;

  //   if (f.discountPercentage > 0) {
  //     doc.setLineWidth(0.5);
  //     doc.setDrawColor(200, 200, 200);
  //     doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //     doc.setFont('helvetica', 'normal');
  //     doc.setFontSize(7);
  //     doc.text(`Discount (${f.discountPercentage}%)`, marginX + 2, y + 4);
  //     doc.text(`-₹${f.discountAmount}`, pageWidth - marginX - 25, y + 4);
  //     y += 7;
  //   }

  //   doc.setLineWidth(0.5);
  //   doc.setDrawColor(200, 200, 200);
  //   doc.rect(marginX, y, pageWidth - marginX * 2, 7, 'S');
  //   doc.setFont('helvetica', 'bold');
  //   doc.setFontSize(8);
  //   doc.text('Total Amount', marginX + 2, y + 4);
  //   doc.text(`₹${f.totalAmount}`, pageWidth - marginX - 25, y + 4);

  //   y += 10;

  //   // Notes section
  //   if (f.notes) {
  //     doc.setFont('helvetica', 'bold');
  //     doc.setFontSize(8);
  //     doc.setTextColor(...darkGray);
  //     doc.text('Notes:', marginX, y);
  //     y += 5;
  //     doc.setFont('helvetica', 'normal');
  //     doc.setFontSize(7);
  //     const notesText = f.notes.replace(/\s+/g, ' ').trim();
  //     const splitNotes = doc.splitTextToSize(notesText, pageWidth - marginX * 2);
  //     splitNotes.forEach((line: string, index: number) => {
  //       doc.text(line, marginX, y + (index * 4));
  //     });
  //     y += splitNotes.length * 4 + 6;
  //   }

  //   // Footer signature
  //   const footerY = Math.max(y + 20, pageHeight - 30);
  //   const sigWidth = 60;
  //   const sigX = pageWidth - marginX - sigWidth;
  //   doc.setDrawColor(...darkGray);
  //   doc.setLineWidth(1);
  //   doc.line(sigX, footerY, pageWidth - marginX, footerY);
  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(10);
  //   doc.setTextColor(...darkGray);
  //   doc.text('Dr. Shailesh Sancheti', sigX, footerY + 7);
  //   doc.setFontSize(7);
  //   doc.text('MD (Homeopathy)', sigX, footerY + 12);
  //   doc.save('bill.pdf');
  // }
}
